import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  ILegalityDialogParams,
  ILegalityDialogResult,
  LegalityDialogComponent,
} from '../legality-dialog/legality-dialog.component';
import { User, UserCredential } from 'firebase/auth';
import { Layout, MaterialButtonType } from '../auth-interfaces';
import { NGX_AUTH_FIREBASEUI_ANIMATIONS } from '../animations';
import { AuthProcessService, AuthProvider } from '../auth-process.service';

interface IProviderDetail {
  provider: AuthProvider;
  svgIcon?: string;
  class?: string;
  label?: string;
  svgClass?: string;
}

@Component({
  selector: 'pt-auth-providers',
  templateUrl: 'auth-providers.component.html',
  styleUrls: ['auth-providers.component.scss'],
  animations: NGX_AUTH_FIREBASEUI_ANIMATIONS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [AuthProcessService],
})
export class AuthProvidersComponent {
  @Input() theme: MaterialButtonType = MaterialButtonType.DEFAULT;
  @Input() layout = Layout.ROW;
  @Input() providers: AuthProvider[] | AuthProvider = AuthProvider.ALL;

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSuccess: EventEmitter<UserCredential | User>;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onError: EventEmitter<Error>;

  @Input() tosUrl: string;
  @Input() privacyPolicyUrl: string;
  dialogRef?: MatDialogRef<LegalityDialogComponent, ILegalityDialogResult>;

  themes = MaterialButtonType;
  authProvider = AuthProvider;

  iconButtonProviders: IProviderDetail[] = [
    {
      provider: AuthProvider.Google,
      svgIcon: 'google-colored',
      label: 'Google',
    },
    {
      provider: AuthProvider.Apple,
      class: 'apple-filled',
      svgIcon: 'apple',
      label: 'Apple',
    },
    {
      provider: AuthProvider.Facebook,
      class: 'facebook-filled',
      svgIcon: 'facebook',
      label: 'Facebook',
    },
    {
      provider: AuthProvider.Twitter,
      class: 'twitter-filled',
      svgIcon: 'twitter',
      label: 'Twitter',
    },
    {
      provider: AuthProvider.Github,
      svgIcon: 'github',
      label: 'GitHub',
    },
    {
      provider: AuthProvider.Microsoft,
      svgIcon: 'microsoft',
      label: 'Microsoft',
    },
    {
      provider: AuthProvider.Yahoo,
      svgIcon: 'yahoo',
      label: 'Yahoo',
    },
  ];

  classicButtonProviders: IProviderDetail[] = [
    {
      provider: AuthProvider.Google,
      class: 'google-classic',
      label: 'Google',
    },
    {
      provider: AuthProvider.Apple,
      class: 'apple-classic',
      label: 'Apple',
    },
    {
      provider: AuthProvider.Facebook,
      class: 'facebook-classic',
      label: 'Facebook',
    },
    {
      provider: AuthProvider.Twitter,
      class: 'twitter-classic',
      label: 'Twitter',
    },
    {
      provider: AuthProvider.Github,
      class: 'github-classic',
      label: 'GitHub',
    },
    {
      provider: AuthProvider.Microsoft,
      class: 'microsoft-classic',
      label: 'Microsoft',
    },
    {
      provider: AuthProvider.Yahoo,
      class: 'yahoo-classic',
      label: 'Yahoo',
    },
  ];

  miniFabProviders: IProviderDetail[] = [
    {
      class: 'google-raised',
      provider: AuthProvider.Google,
      svgIcon: 'google',
    },
    {
      class: 'apple-raised',
      provider: AuthProvider.Apple,
      svgIcon: 'apple',
    },
    {
      class: 'facebook-raised',
      provider: AuthProvider.Facebook,
      svgIcon: 'facebook',
    },
    {
      class: 'twitter-raised',
      provider: AuthProvider.Twitter,
      svgIcon: 'twitter',
      svgClass: 'icon-white',
    },
    {
      class: 'github-raised',
      provider: AuthProvider.Github,
      svgIcon: 'github',
    },
    {
      class: 'microsoft',
      provider: AuthProvider.Microsoft,
      svgIcon: 'microsoft',
    },
    {
      class: 'yahoo-raised',
      provider: AuthProvider.Yahoo,
      svgIcon: 'yahoo',
    },
  ];

  constructor(
    public authProcess: AuthProcessService,
    public dialog: MatDialog
  ) {
    this.onSuccess = authProcess.onSuccessEmitter;
    this.onError = authProcess.onErrorEmitter;
  }

  async processLegalSignUP(authProvider: AuthProvider): Promise<void> {
    if (!this.tosUrl && !this.privacyPolicyUrl) {
      await this.authProcess.signInWith(authProvider);
      return;
    }

    const params: ILegalityDialogParams = {
      tosUrl: this.tosUrl,
      privacyPolicyUrl: this.privacyPolicyUrl,
      authProvider,
    };

    this.dialogRef = this.dialog.open<
      LegalityDialogComponent,
      ILegalityDialogParams,
      ILegalityDialogResult
    >(LegalityDialogComponent, {
      data: params,
    });

    if (!this.dialogRef) {
      return;
    }

    const result = await this.dialogRef.afterClosed().toPromise();

    if (result && result.checked) {
      await this.authProcess.signInWith(authProvider);
    }
    this.dialogRef = undefined;
  }

  hasProvider(authProvider: AuthProvider): boolean {
    return (
      authProvider === AuthProvider.ALL || this.providers.includes(authProvider)
    );
  }
}
